import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46fcd73e-03d2-4567-a4ea-9e3f8b292ac2/artifacts/qlzjjr45_logo-dkr-consulting.png";

export const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { language, toggleLanguage, t } = useLanguage();
    const { theme, toggleTheme } = useTheme();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    const navLinks = [
        { to: '/', label: t('Accueil', 'Home') },
        { to: '/formations', label: t('Formations', 'Education') },
        { to: '/applications', label: 'Applications' },
        { to: '/experiences', label: t('Expériences', 'Experience') },
        { to: '/services', label: 'Services' },
        { to: '/contact', label: 'Contact' },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <>
            <nav 
                data-testid="main-navbar"
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled 
                        ? theme === 'light' 
                            ? 'bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm' 
                            : 'bg-slate-900/95 backdrop-blur-xl border-b border-white/10'
                        : 'bg-transparent'
                }`}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                            <img src={LOGO_URL} alt="DKR CONSULTING" className="h-12 w-auto rounded-full" />
                            <span className={`hidden sm:block font-bold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>
                                DKR CONSULTING
                            </span>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center gap-8">
                            {navLinks.map(link => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
                                    className={`font-semibold transition-colors ${
                                        isActive(link.to) 
                                            ? 'text-blue-500' 
                                            : theme === 'light' 
                                                ? 'text-slate-800 hover:text-blue-500' 
                                                : 'text-white hover:text-blue-400'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </div>

                        {/* Language & Theme Switch */}
                        <div className="flex items-center gap-3">
                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-full bg-slate-800 dark:bg-slate-800 light:bg-white border border-white/10 light:border-slate-200 text-slate-400 hover:text-white transition-colors"
                                data-testid="theme-toggle"
                                aria-label="Toggle theme"
                            >
                                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                            </button>

                            <div className="lang-switch" data-testid="language-switcher">
                                <button
                                    onClick={() => language !== 'fr' && toggleLanguage()}
                                    className={`lang-btn ${language === 'fr' ? 'active' : ''}`}
                                    data-testid="lang-fr"
                                >
                                    FR
                                </button>
                                <button
                                    onClick={() => language !== 'en' && toggleLanguage()}
                                    className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                                    data-testid="lang-en"
                                >
                                    EN
                                </button>
                            </div>

                            {/* Mobile Menu Button */}
                            <button
                                className="lg:hidden p-2 text-white"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                data-testid="mobile-menu-toggle"
                            >
                                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="mobile-menu lg:hidden" data-testid="mobile-menu">
                    {navLinks.map(link => (
                        <Link
                            key={link.to}
                            to={link.to}
                            className={`mobile-menu-link ${isActive(link.to) ? 'text-blue-400' : ''}`}
                            data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>
            )}
        </>
    );
};
